import { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import url from '../../helpers/url';
import { ButtonTypes, COMPONENT_TYPES, ITEM_TYPE, PROGRAM_TYPE, KIDSMODE } from '../../config/constants';
import { __ } from '../../translations';
import h, { getNewContentRating, shouldShowParentalRatingPopup } from '../../helpers/common';
import user from '../../helpers/user';
import viewEvents from '../../helpers/viewEvents';
import subscription from '../../helpers/subscription';
import {
    getItemProperties,
    getMixpanelVodProperties,
    getMixpanelLinearProperties,
} from '../../contexts/MixpanelHelper';
import { EVENT, KEYS } from '../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import { BRAZE } from '../../config/analytics-events';
import useBraze from '../../hooks/useBraze';

import { Button } from '@intigral/shared/components/Button';
import { FontIcon } from '@intigral/shared/components/FontIcon';
import { setSession } from '@intigral/utils/lib/browser-storage/localStorage';
import { getPromotionRoutePath } from '@intigral/utils/lib/helpers/promotion';
import { SESSION_WCP } from '@intigral/constant';
import { appConfig } from '../../config/app.config';
import { showUpgradedModal } from '../../store/reducers/generalConfigurationSlice';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { ParentalLock } from '../elements/ParentalLock';
import useToggleDetailOverlay from '../../hooks/useToggleDetailOverlay';
import { Text } from '@intigral/shared/components/Text';
import useLanguage from '../../hooks/useLanguage';
import playerService from '../../helpers/bitmovinPlayer';
import { checkShowLockIconForPPV } from '../../helpers/vod';
import usePPVManagement from '../../hooks/usePPVManagement';
import { showGuestPromotionPopup } from '../../config/guestMode';

interface PlayVideoButtonProps {
    children?: any;
    className?: string;
    buttonType?: string;
    item: any;
    componentType?: string;
    componentName?: string;
    meta?: any;
    onClick?: () => void;
    nextEpisode?: any;
    fromOverlay?: boolean;
    testId?: string;
    shouldCallMixPanelEvent?: boolean;
    variant?: 'link' | 'button';
    isLoading?: boolean;
}

const PlayVideoButton = ({
    children,
    className,
    buttonType,
    item,
    componentType,
    componentName,
    meta,
    testId,
    onClick = () => {
        return;
    },
    nextEpisode = null,
    fromOverlay = false,
    shouldCallMixPanelEvent = true,
    variant = 'link',
    isLoading = false,
}: PlayVideoButtonProps) => {
    const dispatch = useDispatch();
    const { logBrazeCustomEvent } = useBraze();
    const navigate = useNavigate();
    const { track }: { track: any } = useContext(MixpanelContext);
    const language = useLanguage();
    const { showDetailOverlay, removeSearchParams, showPpvOverlay } = useToggleDetailOverlay();

    // Use PPV management hook for all PPV-related functionality
    const { isPPVItem: checkIsPPVItem, getEnhancedChannel, getPPVStatus } = usePPVManagement();

    if (!item || !Object.keys(item).length) {
        return null;
    }

    let canPlayContent = false;

    const itemRating: string = item?.ratings?.toUpperCase();

    const currentProfileRating: string = user.getProfile()?.newParentalControl?.toUpperCase();

    if (itemRating && currentProfileRating) {
        // An array of rating, like this: ["G", "PG", "PG12", "PG15", "R15", "R18"]
        const contentRatingArray = getNewContentRating();

        // If the current profile rating has higher/equal index then it can play the content.
        canPlayContent = contentRatingArray.indexOf(currentProfileRating) >= contentRatingArray.indexOf(itemRating);
    }

    // Using React Query instead of Redux for configuration
    const { paidVsFree } = useConfigurationSections();
    const enableLockIcon = paidVsFree?.lockIconEnable || false;

    const renderMessage = ({ msgTitle, msgText, msgButtonText, clickURL, onCTAHandler }) => {
        // eslint-disable-next-line
        // @ts-expect-error
        if (typeof $zopim !== 'undefined' && typeof $zopim.livechat !== 'undefined') {
            // eslint-disable-next-line
            // @ts-expect-error
            $zopim.livechat.button.show();
        }
        return (
            <>
                <Text as="h2" variant="h2" margin={{ default: '2x', md: '3x' }}>
                    {msgTitle}
                </Text>
                <Text as="p" alignment="center" color="gray" margin={{ default: '2x', md: '4x' }}>
                    {msgText}
                </Text>
                {clickURL && msgButtonText && user.getProfileMode() !== KIDSMODE.KID && (
                    <Button
                        style={{ width: '100%' }}
                        LinkComponent="a"
                        data-test-id={testId}
                        href={clickURL}
                        // className="overlay-btn"
                        onClick={(e) => {
                            onCTAHandler();
                        }}
                    >
                        {msgButtonText}
                    </Button>
                )}
            </>
        );
    };

    const getPopupMsg = (msgTitle, msgText, msgButtonText?, clickURL?) => {
        const onMount = () => {
            track(EVENT.OVERLAY.ADDON_SUBSCRIPTION.POPUP_VIEW, {
                'Addon Name': msgTitle,
            });
        };

        const onCTAHandler = () => {
            track(EVENT.OVERLAY.ADDON_SUBSCRIPTION.POPUP_SUBSCRIBE, {
                'Addon Name': msgTitle,
            });
        };

        const onUnmount = () => {
            track(EVENT.OVERLAY.ADDON_SUBSCRIPTION.POPUP_DISMISS, {
                'Addon Name': msgTitle,
            });
        };

        const messageComponent = renderMessage({
            msgTitle,
            msgText,
            msgButtonText,
            clickURL,
            onCTAHandler,
        });
        viewEvents.broadcast('openPopup', {
            component: messageComponent,
            onMount,
            onUnmount,
        });
    };

    // TODO: move into a separate hook
    const handleMixpanelEvents = () => {
        const eventProps = {
            [KEYS.CAROUSEL.SCREEN_NAME]: h.getScreen(),
            [KEYS.CAROUSEL.CAROUSEL_NAME]: componentName,
            [KEYS.CAROUSEL.CAROUSEL_TYPE]: h.getCarouselComponentType(componentType),
        };
        switch (componentType) {
            case COMPONENT_TYPES.FEATURED_SLIDER:
                track(`${h.getScreen()} - ${EVENT.PROMOTIONS.PLAY}`, {
                    ...getItemProperties(item),
                    ...eventProps,
                    'Promotion Type': item.promoType,
                    'Promotion Title': item.title || item?.programTitle?.en || '',
                });
                break;
            case COMPONENT_TYPES.VOD_CAROUSEL:
                if (componentName === COMPONENT_TYPES.SEARCH_RECOMMENDATIONS) {
                    track(`${h.getScreen()} - ${EVENT.SEARCH_RESULTS.RECOMMENDATION_ITEM_PLAY}`, {
                        [KEYS.GENERAL.SEARCH_RECOMMENDATION_TYPE]: meta.title.en,
                        ...getMixpanelVodProperties(item),
                        ...eventProps,
                    });
                } else {
                    track(`${h.getScreen()} - ${EVENT.CAROUSEL.ITEM_PLAY}`, {
                        [KEYS.GENERAL.CAROUSLE_NAME]: componentName,
                        ...getMixpanelVodProperties(item),
                        ...eventProps,
                    });
                }
                break;
            case COMPONENT_TYPES.VOD_FEATURED_CAROUSEL:
                track(`${h.getScreen()} - ${EVENT.CAROUSEL.ITEM_PLAY}`, {
                    [KEYS.GENERAL.CAROUSLE_NAME]: componentName,
                    ...getMixpanelVodProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.USER_WATCHLIST:
                track(`${h.getScreen()} - ${EVENT.WATCHLIST.ITEM_PLAY}`, {
                    [KEYS.GENERAL.ITEM_TYPE]: item.category,
                    ...getItemProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.REWIND_CAROUSEL:
                track(`${h.getScreen()} - ${EVENT.REWIND.ITEM_CLICK}`, {
                    ...getMixpanelLinearProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.SEARCH_RESULTS:
                track(`${h.getScreen()} - ${EVENT.SEARCH_RESULTS.ITEM_PLAY}`, {
                    ...(item.programType && {
                        [KEYS.GENERAL.ITEM_TYPE]: ITEM_TYPE[item.programType.toUpperCase()],
                    }),
                    ...getItemProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.DETAILS_CONTAINER:
                track(`${h.getScreen()} - ${EVENT.VOD_DETAILS.WATCH_CLICK}`, {
                    ...getMixpanelVodProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.SEASON_EPISODES:
                track(`${h.getScreen()} - ${EVENT.VOD_DETAILS.EPISODE_PLAY_CLICK}`, {
                    ...getMixpanelVodProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.FULLVOD_CATALOG_LIST:
                track(`${h.getScreen()} - ${EVENT.VOD_LISTING.ITEM_PLAY}`, {
                    ...getMixpanelVodProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.CONTINIE_WATCHING:
                track(`${h.getScreen()} - ${EVENT.CONTINUE_WATCHING.ITEM_PLAY}`, {
                    [KEYS.GENERAL.PROGRAM_TYPE]: item.programType,
                    ...(item.programType === 'VOD'
                        ? getMixpanelVodProperties(item)
                        : getMixpanelLinearProperties(item)),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.WATCHLIST_CAROUSEL:
                track(`${h.getScreen()} - ${EVENT.WATCHLIST_CAROUSEL.ITEM_PLAY}`, {
                    [KEYS.GENERAL.WATCHLIST_TYPE]: item.category,
                    ...getItemProperties(item),
                    ...eventProps,
                });
                break;
            case COMPONENT_TYPES.TV_GUIDE_CAROUSEL:
                track(`${h.getScreen()} - ${EVENT.TV_GUIDE_CAROUSEL.PROGRAM_CLICK}`, {
                    ...getMixpanelLinearProperties(item),
                    ...eventProps,
                });
                break;
            default:
                break;
        }
    };

    const checkUserAllowedToPlay = (e) => {
        if (item && Object.keys(item).length > 0 && !h.hasTVODTag(item)) {
            // eslint-disable-next-line
            const vodItemPackages = item.hasOwnProperty('subscriptionGuids') ? item.subscriptionGuids : null;

            const subscriptionGuid = item?.subscriptionGuids?.[0];

            if ('isLocked' in item && item.isLocked) {
                e.preventDefault();
                if (user.getUserSegment() === 'B2B') {
                    getPopupMsg(
                        subscriptionGuid
                            ? __(`b2bNotSubscribed_popup_header_${subscriptionGuid}`)
                            : __('b2bNotSubscribed_popup_header'),
                        subscriptionGuid
                            ? __(`b2bNotSubscribed_popup_description_${subscriptionGuid}`)
                            : __('b2bNotSubscribed_popup_description')
                    );
                } else {
                    getPopupMsg(
                        __(
                            subscriptionGuid
                                ? `loggedInNoExtraAddon_popup_header_${subscriptionGuid}`
                                : 'loggedInNoExtraAddon_popup_header'
                        ),
                        __(
                            subscriptionGuid
                                ? `loggedInNoExtraAddon_popup_description_${subscriptionGuid}`
                                : 'loggedInNoExtraAddon_popup_description'
                        ),
                        __('loggedInNoExtraAddon_popup_button_title'),
                        user.getUserProfileUrl()
                    );
                }
                return;
            }

            // Check if this is a PPV item using the hook
            const isPPVItemResult = checkIsPPVItem(item, buttonType);

            // For PPV items, we need special handling
            if (isPPVItemResult) {
                const channelId =
                    item?.channelId || item?.channelID || item?.id || item?.channelNo || item?.channelNumber;

                // Get comprehensive PPV status using the hook
                const {
                    isPPV,
                    isLocked: shouldShowLock,
                    enhancedChannel,
                    shouldShowPPVModal,
                } = getPPVStatus(item, channelId);

                if (shouldShowLock) {
                    // PPV not purchased, show upgrade modal
                    e.preventDefault();
                    if (enableLockIcon) {
                        dispatch(showUpgradedModal(true));
                    } else {
                        getPopupMsg(
                            __('loggedInUnSub_popup_header'),
                            __('loggedInUnSub_popup_description1'),
                            __('loggedInUnSub_popup_actionBtn_title'),
                            user.getUserProfileUrl()
                        );
                    }
                    return;
                } else {
                    // PPV is purchased, allow navigation
                    return; // Allow the default navigation behavior
                }
            }

            if (!user.isDefaultUser() && !subscription.checkItemBelongsToActivePackages(vodItemPackages)) {
                e.preventDefault();
                if (enableLockIcon) {
                    dispatch(showUpgradedModal(true));
                } else {
                    getPopupMsg(
                        __('loggedInUnSub_popup_header'),
                        __('loggedInUnSub_popup_description1'),
                        __('loggedInUnSub_popup_actionBtn_title'),
                        user.getUserProfileUrl()
                    );
                }
                return;
            }

            if (!canPlayContent && h.isDeviceSupported()) {
                e.preventDefault();
                // TODO : adding delay to overcome hiding parental popup behind detail overlay
                setTimeout(() => {
                    getPopupMsg(
                        __('livetv_content_not_allowed_title'),
                        __('livetv_content_not_allowed_desc'),
                        __('parental_control_popup_btn_text'),
                        url.route('profile-settings')
                    );
                }, 100);
                return;
            }
        }
        return;
    };

    const handleBrazeEvents = () => {
        const vodTitle = item?.name || item?.textEn || item?.text || item?.title || item?.programTitle?.en || '';
        const tvShowName = item?.seriesTitle || vodTitle;
        const vodType = item?.programType ? ITEM_TYPE[item.programType.toUpperCase()] ?? item.programType : '';
        const vodCategory = item?.categories
            ? Array.isArray(item.categories)
                ? item.categories.join(',')
                : String(item.categories)
            : item?.category ?? '';
        const language = item?.language
            ? Array.isArray(item.language)
                ? item.language.join(',')
                : String(item.language)
            : '';
        const seasonName = String(url.getQuery('season') || item?.tvSeasonNumber || item?.seasonNumber || '');

        if (item.programType === PROGRAM_TYPE.SERIES) {
            logBrazeCustomEvent(BRAZE.EVENTS.VOD_WATCHED, {
                [BRAZE.PROPERTIES.VOD_TYPE]: vodType,
                [BRAZE.PROPERTIES.VOD_CATEGORY]: vodCategory,
                [BRAZE.PROPERTIES.TV_SHOW_NAME]: tvShowName,
                [BRAZE.PROPERTIES.SEASON_NAME]: seasonName,
                [BRAZE.PROPERTIES.GENRE]: item?.genre ?? '',
                [BRAZE.PROPERTIES.LANGUAGE]: language,
            });
        } else if (item.programType === PROGRAM_TYPE.EPISODE) {
            logBrazeCustomEvent(BRAZE.EVENTS.VOD_WATCHED, {
                [BRAZE.PROPERTIES.VOD_TYPE]: vodType,
                [BRAZE.PROPERTIES.VOD_CATEGORY]: vodCategory,
                [BRAZE.PROPERTIES.TV_SHOW_NAME]: tvShowName,
                [BRAZE.PROPERTIES.EPISODE_NAME]: item?.tvSeasonEpisodeNumber ?? '',
                [BRAZE.PROPERTIES.SEASON_NAME]: seasonName,
                [BRAZE.PROPERTIES.GENRE]: item?.genre ?? '',
                [BRAZE.PROPERTIES.LANGUAGE]: language,
            });
        } else {
            logBrazeCustomEvent(BRAZE.EVENTS.VOD_WATCHED, {
                [BRAZE.PROPERTIES.VOD_TYPE]: vodType,
                [BRAZE.PROPERTIES.VOD_CATEGORY]: vodCategory,
                [BRAZE.PROPERTIES.MOVIE_NAME]: vodTitle,
                [BRAZE.PROPERTIES.GENRE]: item?.genre ?? '',
                [BRAZE.PROPERTIES.LANGUAGE]: language,
            });
        }
    };

    const onclickHandler = (e) => {
        e.stopPropagation();
        const currentUser = user.getUserInfo();
        if (showGuestPromotionPopup(e)) {
            return;
        }

        const itemIsLiveTV = item?.category === 'LINEAR' || item?.promoType === 'LIVESTREAM' || false;
        if (itemIsLiveTV) {
            setSession('noAds', 'true');
        }
        if (item?.useCaseId) {
            playerService.setUseCaseId(item?.useCaseId);
        }
        onClick();
        if (shouldCallMixPanelEvent) {
            handleMixpanelEvents();
        }
        handleBrazeEvents();

        const excludedType = ['CHANNEL', 'LIVESTREAM', 'PPV', 'ROUTE'];
        const { promoType } = item;
        if (
            typeof promoType === 'undefined' ||
            (typeof promoType !== 'undefined' && !excludedType.includes(promoType))
        ) {
            checkUserAllowedToPlay(e);
        }
    };

    const getPlayLink = (buttonType, _componentName, item) => {
        const urlQueryParams = {
            id: btoa(item?.Id || item?.id || item?.paId),
            guid: btoa(item.guid),
        } as { season?: any };

        const seasonNumber = item?.tvSeasonNumber || item?.seasonNumber;
        if (item?.programType === PROGRAM_TYPE.EPISODE && seasonNumber) {
            urlQueryParams.season = seasonNumber;
        }

        if (buttonType === 'VodItem-details-overlay') {
            return (
                <>
                    <Button
                        plain
                        LinkComponent={Link}
                        to={url.route('play', urlQueryParams)}
                        state={item}
                        className={className}
                        onClick={onclickHandler}
                        data-test-id={testId}
                        loading={isLoading}
                    >
                        {children}
                        <FontIcon name="play" size="large" md="x-large" />
                    </Button>
                </>
            );
        }

        return (
            <Button
                LinkComponent={Link}
                to={url.route('play', urlQueryParams)}
                className={className}
                onClick={onclickHandler}
                compact
                data-test-id={testId}
                loading={isLoading}
            >
                {children}
                <FontIcon
                    name="play-outline"
                    size="smaller"
                    rotate={language === 'ar' ? 'flip' : null}
                    md="smaller"
                ></FontIcon>
            </Button>
        );
    };

    const openDetailOverlay = () => {
        removeSearchParams(['movie', 'series', 'guid', 'season']);
        const itemId = item.Id;
        const typeKey = item.programType === 'movie' ? 'movie' : 'series';
        showDetailOverlay({
            [typeKey]: btoa(itemId),
            guid: btoa(item?.guid),
            season: btoa(item?.tvSeasonNumber || item?.seasonNumber || null),
        });
    };

    /**
     * Renders either a styled Button or a Link component based on the provided `variant` prop.
     * This function is supposed to replace big `switch` statement in the original code
     *
     * @param link - The destination URL or route for the button or link.
     * @returns A React element: either a Button or Link, configured with the provided props.
     *
     * @todo additional variants will be added to support more button/link types
     *
     * @remarks
     * - If `variant` is `'button'`, returns a Button component with navigation and click handling.
     * - If `variant` is `'link'` or any other value, returns a Link component.
     * - Both components receive `className`, `onClick` handler, `testId`, and `children` props.
     */
    const renderButton = (link: string, state?) => {
        const defaultProps = {
            className,
            onClick: onclickHandler,
        };

        if (testId) {
            defaultProps['data-test-id'] = testId;
        }

        if (state) {
            defaultProps['state'] = state;
        }

        switch (variant) {
            case 'button':
                return (
                    <Button
                        {...defaultProps}
                        LinkComponent={Link}
                        to={link}
                        compact={componentType !== COMPONENT_TYPES.FEATURED_SLIDER}
                        className={className}
                    >
                        {children}
                    </Button>
                );
            case 'link':
            default:
                return (
                    <Link {...defaultProps} to={link}>
                        {children}
                    </Link>
                );
        }
    };

    switch (buttonType) {
        // TODO: is this used anywhere?
        case ButtonTypes.NextEpisode:
            return renderButton(
                url.route('play', {
                    id: btoa(nextEpisode.id),
                    season: nextEpisode.seasonNumber || '',
                }),
                nextEpisode
            );
        // TODO: is this used anywhere?
        case ButtonTypes.SeriesDetails:
            return renderButton(
                url.route('play', {
                    id: btoa(item.Id),
                    guid: btoa(item.guid),
                    season: item.seasonNumber || '',
                }),
                item
            );

        case ButtonTypes.VodDetails:
            return renderButton(
                url.route('play', {
                    id: btoa(item.Id),
                    guid: btoa(item.guid),
                }),
                item
            );
        case ButtonTypes.Stream:
        case ButtonTypes.TvGuideCarouselItemLive:
            return (
                <Link
                    to={url.route('livetv', {
                        channelId: btoa(item.channelId),
                    })}
                    className={className}
                    onClick={onclickHandler}
                >
                    {children}
                </Link>
            );
        case ButtonTypes.VodFeaturedItem:
            if (!item?.promoType) return;
            if (item.promoType === 'PPV') {
                return (
                    <Button
                        className={className}
                        data-test-id={testId}
                        onClick={(e) => {
                            showPpvOverlay({
                                eventId: item?.ppvEvent?.eventId,
                                type: 'Home_Screen',
                            });
                            onclickHandler(e);
                        }}
                    >
                        {children}
                    </Button>
                );
            } else if (['CHANNEL', 'LIVESTREAM', 'PPV'].includes(item.promoType)) {
                return (
                    <Button
                        to={url.route('livetv', {
                            channelId: btoa(item.channelId),
                        })}
                        className={className}
                        onClick={onclickHandler}
                        LinkComponent={Link}
                        data-test-id={testId}
                    >
                        {children}
                    </Button>
                );
            } else if (item.promoType === 'REWIND') {
                return (
                    <Button
                        to={url.route('livetv', {
                            id: btoa(item.Id),
                            stationId: btoa(item.stationId),
                            listingId: btoa(item.listingId),
                        })}
                        className={className}
                        onClick={(e) => {
                            setSession(SESSION_WCP.PROMO_REWIND, item);
                            onclickHandler(e);
                        }}
                        LinkComponent={Link}
                        data-test-id={testId}
                    >
                        {children}
                    </Button>
                );
            } else if (item.promoType === 'CATCHUP') {
                const title = language === 'ar' ? item?.programTitle?.ar || '' : item?.programTitle?.en || '';
                const channelTitle =
                    language === 'ar' ? item?.channel?.channelTitleAr || '' : item?.channel?.channelTitle || '';
                return (
                    <Button
                        className={className}
                        data-test-id={testId}
                        onClick={(e) => {
                            e.preventDefault();
                            onclickHandler(e);
                            if (item?.ratings && shouldShowParentalRatingPopup(item?.ratings)) {
                                viewEvents.broadcast('openPopup', {
                                    component: <ParentalLock />,
                                    screen: 'catchupPlayer',
                                });
                                return;
                            }
                            navigate(`${language ? `/${language}` : ''}/catchup-player`, {
                                state: {
                                    programData: {
                                        title: title,
                                        name: title,
                                        startTime: item?.startTime || 0,
                                        endTime: item?.endTime || 0,
                                        tuningURLs: {
                                            catchUpURL: item?.channel?.tuningURLs?.catchUpURL || '',
                                        },
                                        channelTitle: channelTitle,
                                        channelName: channelTitle,
                                        channelNumber: item?.channel?.channelNumber,
                                        ...(item?.channel?.category &&
                                            Array.isArray(item.channel.category) &&
                                            item.channel.category[0] && {
                                                channelCategory: item.channel.category[0],
                                            }),
                                        poster: item?.image,
                                        guid: item?.guid,
                                        paId: item?.paId,
                                    },
                                },
                            });
                        }}
                    >
                        {children}
                    </Button>
                );
            } else if (item.promoType === 'SEASON') {
                return (
                    <Button
                        to={url.route('play', {
                            id: btoa(item.Id),
                            guid: btoa(item?.seriesId?.split('/')?.pop()),
                            season: item.seasonNumber,
                        })}
                        className={className}
                        onClick={onclickHandler}
                        LinkComponent={Link}
                        data-test-id={testId}
                    >
                        {children}
                    </Button>
                );
            } else if (item.promoType === 'ROUTE' && item.screenId) {
                return (
                    <Button
                        className={className}
                        data-test-id={testId}
                        onClick={(e) => {
                            e.preventDefault();
                            onclickHandler(e);
                            navigate(getPromotionRoutePath(item.screenId, language, appConfig.defaultLanguage));
                        }}
                    >
                        {children}
                    </Button>
                );
            } else {
                return (
                    <Button
                        onClick={onclickHandler}
                        LinkComponent={Link}
                        to={url.route('play', { id: btoa(item.Id), guid: btoa(item.guid) })}
                        data-test-id={testId}
                        className={className}
                    >
                        {children}
                    </Button>
                );
            }
        case ButtonTypes.EpisodeItem:
            return renderButton(
                url.route('play', {
                    id: btoa(item.Id),
                    guid: btoa(item.guid),
                    season: item.tvSeasonNumber ? item.tvSeasonNumber : item.seasonNumber,
                }),
                item
            );
        case ButtonTypes.RewindItem:
            return renderButton(
                url.route(url.getDetailsPage('live'), {
                    id: btoa(item.paId || item.Id),
                    stationId: btoa(item.stationId),
                    listingId: btoa(item.listingId),
                    startDate: btoa(item?.startTime),
                }),
                { rewindItem: item, isRewindItem: true }
            );
        case ButtonTypes.ContinueWatching:
            return item.programType === 'LINEAR'
                ? renderButton(
                      url.route(url.getDetailsPage('live'), {
                          id: btoa(item.Id),
                          stationId: btoa(item.stationId),
                          startDate: btoa(item.startTime),
                      })
                  )
                : renderButton(
                      url.route('play', {
                          id: btoa(item.Id),
                          guid: btoa(item.guid),
                      }),
                      item
                  );
        case ButtonTypes.WatchlistCarouselItem:
            return item.programType === 'Rewind'
                ? renderButton(
                      url.route(url.getDetailsPage('live'), {
                          id: btoa(item.Id),
                          stationId: btoa(item.stationId),
                          startDate: btoa(item?.startTime),
                      })
                  )
                : renderButton(
                      url.route('play', {
                          id: btoa(item.Id),
                          guid: btoa(item.guid),
                      }),
                      item
                  );
        case ButtonTypes.VodeItem:
        case 'VodItem-details-overlay':
            return getPlayLink(buttonType, componentName, item);
        case ButtonTypes.TVodItem:
            return fromOverlay ? (
                <Button onClick={onclickHandler} state={item} className={'primary play-video'} data-test-id={testId}>
                    {children}
                </Button>
            ) : (
                <Button
                    onClick={onclickHandler}
                    LinkComponent={Link}
                    to={fromOverlay ? null : url.route('play', { id: btoa(item.Id), guid: btoa(item.guid) })}
                    state={item}
                    className={'primary play-video'}
                    data-test-id={testId}
                >
                    {children}
                </Button>
            );
        case ButtonTypes.TvGuideCarouselItemRewind:
        case ButtonTypes.ProgramItem:
            return renderButton(
                url.route(url.getDetailsPage('live'), {
                    id: btoa(item.paId || item.Id),
                    stationId: btoa(item.stationId),
                    startDate: btoa(item?.startTime),
                })
            );
        case ButtonTypes.PPVItem:
            return renderButton(
                url.route('livetv', {
                    channelId: btoa(meta.ppvInfo.channelId),
                })
            );
        case ButtonTypes.IsTVODItem:
            return (
                <Button onClick={openDetailOverlay} className={className}>
                    {' '}
                    {children}
                </Button>
            );
        default:
            return renderButton(
                url.route('play', {
                    id: btoa(item.Id),
                    guid: btoa(item.guid),
                }),
                item
            );
    }
};

export default PlayVideoButton;
